import type es from '../es/admin';

/** `admin` in English. Same keys as `es/admin.ts`, enforced by the type. */
const admin: typeof es = {
  scope: 'Operations',
  pagination: {
    page: (n: number) => `Page ${n}`,
    pageOf: (n: number, pages: number) => `Page ${n} of ${pages}`,
    range: (from: number, to: number, total: number) => `${from} to ${to} of ${total}`,
  },
  empty: '—',
  layout: {
    loading: 'Loading the console',
    gateTitle: 'This section is for operations',
    backToDashboard: 'Back to my dashboard',
    gateBody:
      "Your account doesn't have operator permissions. If you think it should, ask whoever manages the platform.",
    brandSuffix: 'Operations',
    navLabel: 'Operations sections',
    nav: {
      overview: 'Overview',
      users: 'Users',
      jobs: 'Jobs',
      agencies: 'Agencies',
      partners: 'Partners',
      workers: 'Workers',
      english: 'English',
    },
  },
  overview: {
    title: 'Overview',
    rangeLabel: 'Range',
    range: {
      d7: '7 days',
      d30: '30 days',
      all: 'All time',
    },
    kpi: {
      sent: 'Sent',
      replyRate: 'Reply rate',
      interviewRate: 'Interview rate',
      queued: 'Queued',
      discardRate: 'Discarded applications',
      realBounce: (pct: string) => `Real bounces ${pct}`,
      topReason: (reason: string) => `Most common: ${reason}`,
      gateFailRate: 'Gate fail rate',
      tokenCost: 'Token cost (USD)',
      tokens: (input: string, output: string) => `${input} in / ${output} out`,
    },
    funnelTitle: 'Funnel',
    funnel: {
      drafts: 'Drafts',
      queued: 'Queued',
      sent: 'Sent',
      replied: 'Replied',
      interviews: 'Interviews',
    },
    trendTitle: 'Sent and replied',
    trendEmpty: 'No sends in the selected range.',
    trendSent: 'Sent',
    trendReplied: 'Replied',
    workersTitle: 'Workers',
    never: 'never',
    lede: 'The business on top, sending operations below. No demo data or team accounts.',
    chart: {
      legend: 'Series and range total',
      keyboardHint: 'Use the arrow keys to move through the dates.',
      showTable: 'View as table',
      date: 'Date',
      week: 'Week',
      weekOf: (date: string) => `Week of ${date}`,
    },
    activity: {
      scope: 'Who comes in and what brings them back',
      title: 'Activity',
      error: 'Could not load activity. The rest of the Overview did load.',
      truncated: 'There is more data than fits; the oldest part was left out.',
      trackingSince: (date: string) => `Visits counted since ${date}`,
      trackingPending: 'Visits start being counted from the next sign-in.',
      kpi: {
        today: 'Active today',
        d7: 'Active in 7 days',
        d30: 'Active in 30 days',
        stickiness: 'Come back',
        stickinessDesc: 'Daily average of actives over the actives of 30 days',
        noData: 'no data',
      },
      trend: {
        title: 'Who comes in each day',
        active: 'Active',
        new: 'New',
        returning: 'Returning',
        plansTitle: 'Active by plan',
        free: 'No plan',
        trial: 'On trial',
        plan: 'On a plan',
        empty: 'No activity in the selected range.',
      },
      activation: {
        scope: 'First week',
        title: 'Activation',
        empty: 'No sign-ups in the range yet.',
        noDataYet: 'no data yet',
        ofLine: (reached: string, of: string, pct: string) => `${reached} of ${of} · ${pct}`,
        signedInNoMembership: (n: string) => `Signed in and did not start: ${n}`,
        steps: {
          returned: 'Came back',
          has_cv: 'Uploaded their CV',
          jobs_browsed: 'Browsed jobs',
          preferences_saved: 'Saved preferences',
          applied: 'Applied',
          trial: 'Started the trial',
          paid: 'Paid',
        } as Record<string, string>,
      },
      apps: {
        scope: 'Mobile app',
        title: 'TBit Jobs on the phone',
        trackingSince: (date: string) => `Platform measured since ${date}. Counts people who open the app signed in; store downloads live in App Store Connect and Play Console.`,
        trackingPending: 'Platform starts being counted with the next app use.',
        kpi: {
          users: 'With the app',
          usersDesc: (ios: string, android: string) => `iPhone ${ios} · Android ${android}`,
          active: 'Active in the app, 7 days',
          activeDesc: (ios: string, android: string) => `iPhone ${ios} · Android ${android}`,
          share: 'Use the app',
          shareDesc: 'Of the 7-day actives, how many used it',
          noData: 'no data',
        },
        installsTitle: 'New in the app',
        activeTitle: 'Active by platform',
        web: 'Web',
        ios: 'iPhone',
        android: 'Android',
        empty: 'Nobody uses the app yet in the chosen range.',
        versionsTitle: 'Versions in use (7 days)',
        versionLine: (platform: string, version: string, people: string) => `${platform} ${version}: ${people}`,
      },
      notifications: {
        scope: 'Notices',
        title: 'How much each notice brings back',
        empty: 'No measured notice has gone out yet.',
        columns: {
          tag: 'Notice',
          sent: 'Sent',
          people: 'People',
          returned: 'Back within 48 h',
          viaLink: 'Came in by the link',
          acted: 'Did something',
        },
        returnedCell: (n: string, of: string, pct: string) => `${n} of ${of} (${pct})`,
        tags: {
          agency_invitation: 'Agency invitation',
          employer_reply: 'Employer reply',
          payment_failed: 'Failed payment',
          english_requested: 'English test requested',
          english_reminder: 'English reminder',
          english_retry: 'English retry',
          english_result: 'English result',
        } as Record<string, string>,
      },
      usage: {
        scope: 'Usage',
        pagesTitle: 'Most viewed pages',
        actionsTitle: 'Most done actions',
        empty: 'No data yet.',
        line: (people: string) => `${people} people`,
        pages: {
          panel: 'Dashboard',
          postulaciones: 'Applications',
          mensajes: 'Messages',
          'mensajes/:id': 'A message',
          perfil: 'Profile',
          'perfil/hoja-de-vida': 'CV',
        } as Record<string, string>,
        actions: {
          cv_uploaded: 'Uploaded their CV',
          cv_written: 'Wrote their CV',
          profile_edited: 'Edited their profile',
          jobs_browsed: 'Browsed jobs',
          applied_manual: 'Applied by hand',
          drafts_generated: 'Generated drafts',
          demo_sent: 'Sent the demo',
          auto_send_activated: 'Turned on sending',
          checkout_opened: 'Opened checkout',
          preferences_saved: 'Saved preferences',
          talent_pool: 'Joined the talent pool',
          message_replied: 'Replied to a message',
          invitation_answered: 'Answered an invitation',
          payment_recovered: 'Recovered a payment',
        } as Record<string, string>,
      },
    },
    business: {
      scope: 'Business',
      title: 'How TBit Jobs is doing',
      note: 'No demo data, team accounts or operators. "Paying", "On trial" and "Courtesy" use the same rule as the partner panel.',
      error: "Couldn't load the business numbers. Operations below did load.",
      review: 'Review',
      noStripeValue: 'No data',
      versus: {
        d7: 'vs. the previous 7 days',
        d30: 'vs. the previous 30 days',
      },
      delta: {
        up: (pct: string) => `▲ ${pct}`,
        down: (pct: string) => `▼ ${pct}`,
        same: 'Same',
        fromZero: '▲ from zero',
      },
      kpi: {
        registrations: 'New sign-ups',
        withCv: 'With a resume',
        trials: 'Trials started',
        paying: 'Paying now',
        courtesies: 'Active courtesies',
        cancellations: 'Cancellations',
        abandoned: 'Abandoned checkouts',
        mrr: 'Monthly revenue (MRR)',
      },
      desc: {
        ofRegistrations: (pct: string) => `${pct} of sign-ups in range`,
        inTrialNow: (n: number) => `${n} on trial today`,
        firstCharges: (n: number) => (n === 1 ? '1 first charge in range' : `${n} first charges in range`),
        today: 'Today',
        cancellationsSplit: (paid: number, trial: number) => `${paid} were paying · ${trial} on trial`,
        abandonedSessions: (n: number) => (n === 1 ? '1 unpaid session' : `${n} unpaid sessions`),
        abandonedTruncated: 'There may be more: Stripe was read up to a cap',
        noStripe: 'No Stripe data',
        mrrPaying: (n: number) => `${n} paying`,
        mrrListPrice: (n: number) => `${n} at list price`,
        mrrUnpriced: (n: number) => `${n} without a monthly price`,
      },
      funnel: {
        scope: 'Sign-ups in range',
        title: 'Conversion funnel',
        registered: 'Sign-up',
        withCv: 'Resume',
        trial: 'Trial started',
        firstCharge: 'First charge',
        step: (pct: string) => `${pct} of the previous step`,
        ofRegistered: (pct: string) => `${pct} of sign-ups`,
        empty: 'Nobody signed up in this range.',
      },
      origin: {
        scope: 'Sign-ups in range',
        title: 'Source',
        columns: { origin: 'Source', registered: 'Sign-ups', trial: 'Trial', paying: 'Paid' },
        labels: { direct: 'Direct', partner: 'Partner', coupon: 'Coupon', courtesy: 'Courtesy' },
        topPartners: 'Top partners',
        noPartners: 'No sign-ups with a partner code in this range.',
        partnerLine: (registered: string, trial: string, paying: string) =>
          `${registered} sign-ups · ${trial} on trial · ${paying} paid`,
      },
      trend: {
        title: 'Sign-ups, trials and first charges',
        byDay: 'Per day',
        byWeek: 'Per week',
        registrations: 'Sign-ups',
        trials: 'Trials',
        firstCharges: 'First charges',
        empty: 'No activity in this range.',
      },
      partners: {
        scope: 'Partner program',
        title: 'Partners',
        active: 'Active',
        pending: 'Awaiting approval',
        referrals: 'Referrals',
        referralsRange: (n: string) => `${n} in range`,
        paid: 'Commissions paid',
        due: 'Owed',
        failed: (usd: string) => `${usd} with a failed transfer`,
      },
      agencies: {
        scope: 'Employers',
        title: 'Agencies',
        new: 'New',
        total: 'Total',
        active: 'Active',
        pending: 'Awaiting approval',
        openPositions: 'Open positions',
      },
      recent: {
        scope: 'Operator console',
        title: 'Latest sign-ups',
        columns: { date: 'Date', email: 'Email', origin: 'Source', plan: 'Plan', cv: 'Resume' },
        cvYes: 'Yes',
        cvNo: 'No',
        empty: 'No sign-ups yet.',
        state: {
          paying: 'Paying',
          trial: 'On trial',
          no_trial: 'No active trial',
          canceled: 'Canceled',
          courtesy: 'Courtesy',
        },
      },
    },
    ops: {
      scope: 'Operations',
      title: 'Application sending',
      qualityLast30: 'Last 30 days',
      ofPrevious: (pct: string) => `${pct} of the previous step`,
    },
  },
  jobs: {
    title: 'Job postings',
    syncing: 'Syncing…',
    syncDol: 'Sync DOL',
    searchPlaceholder: 'Search title, company, location',
    status: {
      any: 'Status',
      active: 'Active',
      held: 'Held (Canada)',
      archived: 'Archived',
    },
    source: {
      any: 'Source',
      dolH2b: 'DOL H2B',
      dolH2a: 'DOL H2A',
      csv: 'CSV',
      manual: 'Manual',
      jobbank: 'Job Bank (Canada)',
      euresOpen: 'EURES open (non-EU welcome)',
      euresCodc: 'EURES shortage trades (Spain)',
      eures: 'EURES (EU papers)',
    },
    columns: {
      title: 'Title',
      company: 'Company',
      location: 'Location',
      source: 'Source',
      status: 'Status',
      assigned: 'Assigned',
      applications: 'Applications',
      gate: 'Gate',
      created: 'Created',
    },
    archive: 'Archive',
    reactivate: 'Reactivate',
    emptyTitle: 'No postings',
    emptyNote: 'Sync DOL or create one manually.',
  },
  users: {
    title: 'Users',
    invite: '+ Invite operator',
    stats: {
      total: 'Accounts',
      totalDesc: 'Registered candidates',
      sending: 'Sending',
      sendingDesc: 'Active plan and sending on',
      trial: 'On trial',
      trialDesc: 'Free 7 days in progress',
      noPlan: 'Active, no plan',
      noPlanDesc: 'Nothing goes out for them',
      stopped: 'Stopped',
      stoppedDesc: 'Paused or disconnected',
      filterHint: 'Click to show only these accounts; click again to clear.',
    },
    searchPlaceholder: 'Search by email or name',
    referral: {
      any: 'Referrals',
      onlyReferred: 'Referred only',
      byCode: (code: string) => `Referred by ${code}`,
      by: (name: string | null, code: string, community: string | null) =>
        `Referred by ${name ? `${name} (${code})` : code}${community ? ` · ${community}` : ''}`,
      filterHint: 'Click to see everyone this partner referred.',
    },
    status: {
      any: 'Status',
      active: 'Active',
      paused: 'Paused',
      disconnected: 'Disconnected',
    },
    role: {
      any: 'Role',
      user: 'User',
      operator: 'Operator',
    },
    columns: {
      email: 'Email',
      created: 'Signed up',
      status: 'Status',
      role: 'Role',
      plan: 'Plan',
      visa: 'Visa',
      today: 'Today',
      total: 'Total',
      replies: 'Replies',
      limit: 'Limit',
      lastSent: 'Last sent',
      access: 'Access',
      cv: 'Resume',
    },
    /** La columna Hoja de vida: el puntaje 0-100 que ve el candidato en su panel. */
    cv: {
      none: 'No resume',
      unscored: 'Not scored',
      scoredAt: (date: string) => `Scored on ${date}`,
    },
    visaBoth: 'Both',
    access: {
      trial: (date: string) => (date ? `On trial until ${date}` : 'On trial'),
      paying: 'Paying',
      courtesy: (date: string) => (date ? `Comp until ${date}` : 'Comp'),
      canceled: 'Canceled',
      neverTried: "Didn't start the trial",
      trialUnpaid: "Tried, didn't pay",
      demos: (n: number) => `${n} of 3 samples opened`,
      sendOff: 'Sending off',
      sendOffHint: "Has a plan but never pressed «Start sending»: nothing goes out to employers.",
    },
    actions: {
      pause: 'Pause',
      resume: 'Resume',
      removeOperator: 'Remove operator',
      promoteOperator: 'Make operator',
      comp: 'Comp subscription',
      checkoutTitle: 'Generate checkout',
      viewDetail: 'View details',
      viewAs: 'View as',
      viewAsHint: 'Opens their app exactly as they see it, in a new tab, without being able to change anything.',
      platform: 'Platform',
      platformHint: 'Opens their chat in the TBit Jobs agent on platform.tbit.app, in a new tab.',
      platformMissing: "Not in the TBit Jobs agent on the platform yet: the sync runs every 2 minutes.",
      publicProfile: 'Public profile',
      publicProfileHint: 'Opens their public profile on jobs.tbit.app/perfiles, in a new tab.',
    },
    viewAsError: "Couldn't open this person's view.",
    noResults: 'No results.',
    checkoutCopied: (url: string) => `Checkout URL copied to clipboard:\n${url}`,
    checkoutError: (message: string) => `Couldn't generate checkout: ${message}`,
    checkoutTermsRequired:
      "No link generated: this person hasn't accepted the current version of the TBit Jobs Terms and Conditions yet. Ask them to sign in to jobs.tbit.app and accept it when starting the trial or paying.",
  },
  agencies: {
    title: 'Agencies',
    lede: 'Approve candidate access and manage the status of each agency.',
    searchPlaceholder: 'Search agency or email',
    filterLabel: 'Agency status',
    filter: {
      all: 'All',
      pendingAccess: 'Awaiting access',
      pending: 'Pending',
      active: 'Active',
      suspended: 'Suspended',
    },
    columns: {
      name: 'Name',
      createdBy: 'Created by',
      created: 'Created',
      openPositions: 'Open positions',
      seats: 'Seats',
      status: 'Status',
      accessRequested: 'Requested access',
    },
    website: 'Website',
    actions: {
      approve: 'Approve',
      suspend: 'Suspend',
      reactivate: 'Reactivate',
    },
    confirm: 'Confirm?',
    yes: 'Yes',
    no: 'No',
    emptyTitle: 'No agencies',
    emptyNote: 'No agencies match this filter.',
  },
  workers: {
    title: 'Workers',
    rel: {
      never: 'never',
      now: 'just now',
      minutes: (n: number) => `${n} min ago`,
      hours: (n: number) => `${n} h ago`,
      days: (n: number) => `${n} d ago`,
    },
    names: {
      send: 'Application sending',
      dolSync: 'DOL job sync',
      bounceScanner: 'Bounce detector',
      replyScanner: 'Reply detector',
      jobBankSync: 'Job Bank expiry',
      jobBankDiscovery: 'Job Bank discovery',
      euresSync: 'EURES expiry',
      euresDiscovery: 'EURES discovery',
    },
    lastRun: 'Last run:',
    duration: 'Duration:',
    durationMs: (ms: number) => `${ms} ms`,
    processed: 'Processed:',
    failed: 'Failed:',
    staleThreshold: (min: number) => `Stale threshold: ${min} min`,
    lastError: 'Last error',
  },
  compModal: {
    title: 'Comp subscription',
    plan: 'Plan',
    months: 'Months',
    reason: 'Reason',
    reasonPlaceholder: 'Choose a reason',
    reasons: {
      paid_offline: 'Paid outside the app',
      courtesy: 'Courtesy',
      compensation: 'Compensation',
    },
    amount: 'Amount paid (USD)',
    reasonRequired: 'Choose a reason for the comp.',
    amountRequired: 'Enter how much they paid, in USD (more than 0).',
    note: 'Note (optional)',
    cancel: 'Cancel',
    grant: 'Grant',
  },
  inviteModal: {
    title: 'Invite operator',
    body: "Create operator access for an outside email. If they haven't signed up yet, they'll get access the first time they sign in.",
    emailPlaceholder: 'email@domain.com',
    cancel: 'Cancel',
    create: 'Create',
  },
  drawer: {
    close: '← Close',
    loadError: "Couldn't load.",
    autoSend: (on: boolean) => `Auto-send: ${on ? 'on' : 'off'}`,
    actionsTitle: 'Admin actions',
    busy: '…',
    pauseUser: 'Pause user',
    resumeUser: 'Resume user',
    stopSending: 'Stop sending',
    startSending: 'Start sending',
    grantSubscription: 'Grant subscription',
    removeOperator: 'Remove operator',
    promoteOperator: 'Make operator',
    periodTitle: 'Override subscription period',
    periodStart: 'Start',
    periodEnd: 'Expires',
    applyPeriod: 'Apply period',
    discard: 'Discard',
    periodHint:
      'Leave a date empty to clear it. Expires controls when the user loses the plan.',
    preferences: 'Preferences',
    voiceProfile: 'Voice profile',
    compHistory: 'Comp history',
    compEntry: (plan: string, granted: string, expires: string) =>
      `${plan} · granted ${granted} · expires ${expires}`,
    compNoReason: 'No reason',
    compAmount: (amount: string) => `USD ${amount}`,
    recentApplications: 'Recent applications',
  },
  partners: {
    title: 'Partners',
    lede: "Approve or reject applications to the Partner program. The amount is fixed: USD 7 off the referral's first month and USD 5 per paid month for the partner. Pausing or terminating doesn't stop what was already earned: it keeps being paid. To stop the money, use Hold payouts.",
    filterLabel: 'Partner status',
    filter: {
      all: 'All',
      pending: 'Pending',
      active: 'Active',
      paused: 'Paused',
      rejected: 'Rejected',
      terminated: 'Terminated',
    },
    columns: {
      partner: 'Partner',
      code: 'Code',
      promotion: 'How they will promote',
      referrals: 'Referrals',
      commissions: 'Commissions',
      payouts: 'Payouts',
      status: 'Status',
    },
    payouts: { none: 'Not set up', pending: 'Pending', ready: 'Ready' },
    commissions: (paid: string, pending: string) => `${paid} paid · ${pending} to be paid`,
    actions: {
      approve: 'Approve',
      reject: 'Reject',
      pause: 'Pause',
      reactivate: 'Reactivate',
      terminate: 'Terminate',
      hold: 'Hold payouts',
      release: 'Release payouts',
    },
    confirm: 'Sure?',
    yes: 'Yes',
    no: 'No',
    rejectReason: "Reason for rejecting (we'll email it to them)",
    rejectSend: 'Reject with this reason',
    cancel: 'Cancel',
    rejectedBecause: (reason: string) => `Reason: ${reason}`,
    terminateReason: 'Reason for terminating (it is recorded)',
    terminateSend: 'Terminate with this reason',
    holdReason: 'Reason for the hold (suspected fraud or breach)',
    holdSend: 'Hold with this reason',
    holdInFlight: (n: number) =>
      `Payouts held. ${n === 1 ? 'One transfer was already going out' : `${n} transfers were already going out`} and the hold doesn't stop ${n === 1 ? 'it' : 'them'}: review in Stripe and reverse if needed.`,
    terminatedOn: (date: string, reason: string) => `Terminated on ${date}. Reason: ${reason}`,
    held: (date: string, reason: string, by: string | null) =>
      `Payouts held on ${date}${by ? ` by ${by}` : ''}. Reason: ${reason}`,
    termsVersion: (version: string) => `Terms v. ${version}`,
    errors: {
      codeTaken: 'That code already exists in Stripe. Ask the partner to pick another one.',
      invalidTransition: 'The partner changed status. Reload the page.',
      stripe: "Stripe didn't respond. Try again.",
      failed: "The action couldn't be completed.",
    },
    emptyTitle: 'No applications',
    emptyNote: 'When someone applies to the program, it shows up here and you get an email.',
  },
  status: {
    ACTIVE: 'Active',
    PENDING: 'Pending',
    SUSPENDED: 'Suspended',
    PAUSED: 'Paused',
    DISCONNECTED: 'Disconnected',
    ARCHIVED: 'Archived',
    HELD: 'Held',
    OPERATOR: 'Operator',
    USER: 'User',
    ok: 'OK',
    stale: 'Stale',
    error: 'Error',
    unknown: 'Unknown',
    DRAFT: 'Draft',
    QUEUED: 'Queued',
    SENT: 'Sent',
    REPLIED: 'Replied',
    INTERVIEW: 'Interview',
    REJECTED: 'Rejected',
    TERMINATED: 'Terminated',
  },
};

export default admin;
